/**
 * Formats a date using UTC calendar fields rather than the viewer's local
 * timezone.
 *
 * Heartbeat's "day" buckets are keyed to UTC midnight (see the day rollup in
 * backend/routes/heartbeatRoutes.js), so any date label shown next to
 * bucketed data has to read the same way or it silently disagrees with
 * which bucket the data is actually in. A reply at 2026-09-01T00:30:00Z
 * belongs to the "Sep 1" UTC bucket; `toLocaleDateString()` in a timezone
 * west of UTC (e.g. New York) renders that same instant as "Aug 31" - and
 * renders the bucket's own label the same wrong way, so the timeline column
 * and the reply inside it visibly disagree.
 */
export function formatUTCDate(date, options) {
	// A missing or unparseable date must not render as the literal string
	// "Invalid Date" in the middle of a sentence - which is exactly what the run
	// page showed ("Taken Invalid Date") when the API omitted a field. Callers
	// can decide what to put in its place; returning null lets them.
	if (date === null || date === undefined || date === '') return null;
	const d = new Date(date);
	if (Number.isNaN(d.getTime())) return null;
	return d.toLocaleDateString(undefined, { timeZone: 'UTC', ...options });
}

/**
 * "5 minutes ago", "2 hours ago".
 *
 * The step table pairs each unit with how many of it make the NEXT unit, and
 * the unit advances with the division. Getting that pairing wrong is easy and
 * silent: an earlier version took the unit name from the row it was dividing
 * BY rather than the row it landed on, so 61 minutes rendered as "1 minute
 * ago" - a run from an hour earlier looked like it had just happened.
 *
 * Returns null for a missing or unparseable value, like formatUTCDate.
 */
const RELATIVE_STEPS = [
	['second', 60],
	['minute', 60],
	['hour', 24],
	['day', 7],
	['week', 4.345],
	['month', 12],
	['year', Infinity],
];

export function relativeTime(date, now = Date.now()) {
	if (date === null || date === undefined || date === '') return null;
	const then = new Date(date).getTime();
	if (Number.isNaN(then)) return null;

	let value = Math.round((now - then) / 1000);
	let i = 0;
	while (i < RELATIVE_STEPS.length - 1 && Math.abs(value) >= RELATIVE_STEPS[i][1]) {
		value = Math.round(value / RELATIVE_STEPS[i][1]);
		i += 1;
	}
	// Negative because Intl counts forward: -5 minutes is "5 minutes ago".
	return new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' }).format(-value, RELATIVE_STEPS[i][0]);
}
